import { FC, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  CategoryFormType,
  CategorySchema,
  defaultCategoryFormValues,
} from './utils/CategorySchema';
import { zodResolver } from '@hookform/resolvers/zod';
import DialogWrapper from '@/components/shared/DialogWrapper';
import { useMutation } from '@tanstack/react-query';
import { editCategoryMutationFn, useCategory, useCategories } from '@/api/category/category.api';
import { AxiosError } from 'axios';
import { CategoriesForm } from './components/CategoriesForm';
import { toast } from 'sonner';
import SuccessToastText from '@/components/shared/SuccessToastText';
import { SONNER_ERROR_OPTIONS, SONNER_SUCCESS_OPTIONS } from '@/common/constants';
import * as Sentry from '@sentry/react';
import { CategoryApi } from '@/api/category/category.model';
import { UUID } from 'crypto';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ErrorResponse } from '@/api/types';
import { paths } from '@/routes/paths';
import { EditFormSkeleton } from '@/components/shared/EditFormSkeleton';
import { getParamsWithAnimation } from '@/lib/utils';

type CategoriesEditParams = {
  id: CategoryApi['id'];
};

const CategoriesEdit: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams() as CategoriesEditParams;
  const {
    data: category,
    isLoading: isCategoryLoading,
    refetch: refetchCategory,
    error,
  } = useCategory(id);
  const { refetch: refetchCategories } = useCategories();

  const form = useForm<CategoryFormType>({
    defaultValues: category
      ? {
          name: category.name,
        }
      : defaultCategoryFormValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(CategorySchema),
  });

  const { mutate: editCategory, isPending } = useMutation<
    CategoryApi,
    AxiosError<ErrorResponse>,
    [UUID, CategoryFormType]
  >({
    mutationFn: editCategoryMutationFn,
    onSuccess: (data: CategoryApi) => {
      refetchCategories();
      toast(
        <SuccessToastText>
          ¡Categoria <span className="text-blue-700">{data.name}</span> fue editada exitosamente!
        </SuccessToastText>,
        SONNER_SUCCESS_OPTIONS,
      );
      handleDismiss();
      refetchCategory();
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      Sentry.captureException(error);

      if (error.response && error.response.data.type === 'DUPLICATED_ERROR') {
        form.setError('name', {
          type: 'validate',
          message:
            'El nombre ingresado ya fue utilizado previamente. Intentá nuevamente con un nombre personalizado.',
        });
      } else {
        toast.error('Ha ocurrido un error durante la edición de la Categoría.', {
          description: 'Por favor, revisa los datos ingresados e intentá de nuevo.',
          ...SONNER_ERROR_OPTIONS,
        });
      }
    },
  });

  useEffect(() => {
    if (category) {
      form.reset({
        name: category.name,
      });
    }
  }, [category]);

  if (error) {
    return <Navigate to="*" replace={true} />;
  }

  const handleDismiss = () =>
    navigate(`${paths.CATEGORIES}${getParamsWithAnimation(location.search, true)}`);

  const handleEditCategory: SubmitHandler<CategoryFormType> = (formData) => {
    if (category) {
      editCategory([category.id, formData]);
    } else {
      toast.error('Lo sentimos, no tenés ningúna Categoría seleccionada.', {
        description: 'Por favor, seleccioná una Categoría e intentá de nuevo.',
        ...SONNER_ERROR_OPTIONS,
      });
      handleDismiss();
    }
  };

  return (
    <DialogWrapper
      title="Editar Categoría"
      subtitle="Las categorías podrán ser asociadas a multiples Centros de Costos, asignando un monto a cada uno de ellos."
      className="w-[425px]"
      onDismiss={handleDismiss}
      onOverlayClick={handleDismiss}
    >
      {isCategoryLoading || !category ? (
        <EditFormSkeleton inputRows={2} />
      ) : (
        <CategoriesForm
          form={form}
          submitLabel="Editar Categoría"
          cancelLabel="Cancelar"
          isLoading={isPending}
          submitHandler={handleEditCategory}
          dismissHandler={handleDismiss}
        />
      )}
    </DialogWrapper>
  );
};

export default CategoriesEdit;
